import React, { useEffect, useMemo, useState } from 'react';
import {
  TableContainer, Table, TableHead, TableRow, TableCell, TableSortLabel, TableBody,
  Paper, Checkbox, TextField
} from '@mui/material';
import config from './config.js';
import './css/sonsberg.css';

const serverUrl = config.serverUrl;
const serverPort = config.serverPort;

const AdminUserScreen = () => {
  const [usersData, setUsersData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('username');
  const [usernameFilter, setUsernameFilter] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchUsersData = async () => {
    try {
    const response = await fetch(`${serverUrl}:${serverPort}/be/user/getAllUsers`, {
        method: 'POST',
        headers: {
            'Authorization': `${token}`
        }, 
        });
        if (response.ok) {
            const data = await response.json();
            if (data && data.users && data.users.length > 0) {
              setUsersData(data.users);
            }
              }
    } catch (error) {
      console.error('Fehler beim Abrufen der Benutzerdaten:', error);
    }
  };

  const handleAdminCheckboxChange = async (userId, isAdmin) => {
    try {
      const response = await fetch(`${serverUrl}:${serverPort}/be/user/updateUser/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify({ isAdmin: !isAdmin }),
      });
      const data = await response.json();
      if (data && data.success) {
        // Update the state with the updated user data
        setUsersData(prevState => prevState.map(user => {
          if (user._id === userId) {
            return { ...user, isAdmin: !isAdmin };
          }
          return user;
        }));
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Benutzers:', error);
    }
  };

  const handleSupervisorCheckboxChange = async (userId, isSupervisor) => {
    try {
      const response = await fetch(`${serverUrl}:${serverPort}/be/user/updateUser/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify({ isSupervisor: !isSupervisor }),
      });
      const data = await response.json();
      if (data && data.success) {
        // Update the state with the updated user data
        setUsersData(prevState => prevState.map(user => {
          if (user._id === userId) {
            return { ...user, isSupervisor: !isSupervisor };
          }
          return user;
        }));
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Benutzers:', error);
    }
  };

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleUsers = useMemo(() => {
    const filtered = usersData.filter((user) =>
      user.username?.toLowerCase().includes(usernameFilter.toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      let result;
      if (orderBy === 'username') {
        result = (a.username || '').localeCompare(b.username || '');
      } else {
        result = (a[orderBy] === b[orderBy]) ? 0 : (a[orderBy] ? -1 : 1);
      }
      return order === 'asc' ? result : -result;
    });

    return sorted;
  }, [usersData, usernameFilter, order, orderBy]);

  return (
    <div className="admin-user-container">
      <h2>Admin User Screen</h2>

      <div className="admin-user-filters">
        <label className="admin-user-filter-label" htmlFor="admin-user-search">User suchen:</label>
        <TextField
          id="admin-user-search"
          className="admin-user-filter-username"
          size="small"
          variant="standard"
          value={usernameFilter}
          onChange={(e) => setUsernameFilter(e.target.value)}
          InputProps={{ disableUnderline: true }}
          fullWidth
        />
      </div>

      <TableContainer component={Paper} className="admin-user-table-container">
        <Table size="small" className="admin-user-table" stickyHeader>
          <TableHead>
            <TableRow className="admin-user-table-header-row">
              <TableCell className="admin-user-cell-name" sortDirection={orderBy === 'username' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'username'}
                  direction={orderBy === 'username' ? order : 'asc'}
                  onClick={() => handleSortRequest('username')}
                >
                  Username
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" className="admin-user-cell-narrow" sortDirection={orderBy === 'isAdmin' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'isAdmin'}
                  direction={orderBy === 'isAdmin' ? order : 'asc'}
                  onClick={() => handleSortRequest('isAdmin')}
                >
                  Admin
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" className="admin-user-cell-narrow" sortDirection={orderBy === 'isSupervisor' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'isSupervisor'}
                  direction={orderBy === 'isSupervisor' ? order : 'asc'}
                  onClick={() => handleSortRequest('isSupervisor')}
                >
                  <span className="admin-user-header-full">Aufsicht</span>
                  <span className="admin-user-header-short">Aufsicht</span>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.map((user) => (
              <TableRow key={user._id} className="admin-user-table-body-row">
                <TableCell className="admin-user-cell-name">{user.username}</TableCell>
                <TableCell align="center" className="admin-user-cell-narrow">
                  <Checkbox
                    size="small"
                    checked={user.isAdmin}
                    onChange={() => handleAdminCheckboxChange(user._id, user.isAdmin)}
                  />
                </TableCell>
                <TableCell align="center" className="admin-user-cell-narrow">
                  <Checkbox
                    size="small"
                    checked={user.isSupervisor}
                    onChange={() => handleSupervisorCheckboxChange(user._id, user.isSupervisor)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AdminUserScreen;
